import React from 'react';
import {
  Box,
  RadioGroup,
  Radio,
  Divider,
  FormControlLabel,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import { CheckOutlined } from '@material-ui/icons';
import { Controller } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { CustomizedTypography } from '@components/Customized';
// import useRequest from '@hooks/useRequest';
import { CapaServiceType } from '@generated/graphql';
import * as assets from '@assets';
import { useStyles } from './styles';
// import { useParams } from 'react-router-dom';

interface AccountTypeSelectorProps {
  setAccountType: (state: CapaServiceType) => void;
  accountType: CapaServiceType;
}

const benefits = [
  '무료 견적요청서 무제한 작성',
  '도면 전용 클라우드로 도면 관리 및 공유',
  '무료로 고객 수주 무제한 받기'
];

/*
  라디오 버튼 선택 시 액션
  - Form의 'accountType' 값 업데이트
  - 부모 컴포넌트(SignupPresenter)의 'accountType' 상태 업데이트

  선택된 라디오버튼 스타일: 부모에서 관리하는 accountType 상태에 따라 className 부여
    - 해당 라디오버튼 value와 현재 accountType이 일치하는 경우 'selected' 부여

  파트너 선택 불가능 조건 : 비 로그인 상태로 RFQ작성 후 회원가입하는 경우
    - 리덕스로 관리하는 requestState의 isComplete 값이 true 인 경우 파트너 라디오버튼 disabled 처리
*/

const AccountTypeSelector: React.FC<AccountTypeSelectorProps> = ({
  setAccountType,
  accountType
}: AccountTypeSelectorProps) => {
  const { accountTypeStyle, radioBox } = useStyles();
  const { control, setValue } = useFormContext();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const partnerDisabled = false;

  return (
    <Box display="flex" className={accountTypeStyle}>
      <Controller
        name="service"
        control={control}
        defaultValue={CapaServiceType.Quotation}
        render={(props) => {
          return (
            <RadioGroup
              {...props}
              onChange={(e) => {
                props.onChange(e);
                setAccountType(e.currentTarget.value as CapaServiceType);
              }}
            >
              <Box
                className={`${radioBox} ${
                  accountType === CapaServiceType.Quotation && 'selected'
                }`}
                onClick={(e) => {
                  setValue('accountType', CapaServiceType.Quotation);
                  setAccountType(CapaServiceType.Quotation);
                }}
              >
                <FormControlLabel
                  value={CapaServiceType.Quotation}
                  control={
                    <Radio
                      color="primary"
                      size={matches ? 'small' : 'medium'}
                    />
                  }
                  labelPlacement={matches ? 'end' : 'bottom'}
                  label={
                    <Box>
                      <img
                        src={assets.LOGO_SMALL}
                        alt="CAPA"
                        width={48}
                        height={13}
                      />
                      <CustomizedTypography
                        className="radioBoxTitle"
                        fontWeight="700"
                      >
                        {matches ? '고객으로 회원가입' : '고객 회원가입'}
                      </CustomizedTypography>
                      <CustomizedTypography
                        className="radioBoxDetail"
                        fontWeight="normal"
                      >
                        {`양산품/시제품/도면 등을 제작해줄
                파트너를 찾고 있어요`}
                      </CustomizedTypography>
                      {!matches && (
                        <>
                          <Divider className="radioBoxDivider" />
                          {benefits.map((benefit, index) =>
                            index !== 2 ? (
                              <RadioBoxItem
                                key={'benefit' + index}
                                className="radioBoxItem"
                              >
                                <CheckOutlined color="primary" />
                                <CustomizedTypography
                                  variant="body2"
                                  color="textPrimary"
                                  fontWeight="500"
                                  noWrap
                                >
                                  {benefit}
                                </CustomizedTypography>
                              </RadioBoxItem>
                            ) : (
                              <React.Fragment key={'benefit' + index} />
                            )
                          )}
                        </>
                      )}
                    </Box>
                  }
                />
              </Box>
              <Box
                className={`
                ${radioBox} ${
                  !partnerDisabled &&
                  accountType === CapaServiceType.QuotationPartner
                    ? 'selected'
                    : ''
                } ${partnerDisabled && 'disabled'}`}
                onClick={(e) => {
                  setValue('accountType', CapaServiceType.QuotationPartner);
                  setAccountType(CapaServiceType.QuotationPartner);
                  // }
                }}
              >
                <FormControlLabel
                  disabled={partnerDisabled}
                  value={CapaServiceType.QuotationPartner}
                  control={
                    <Radio
                      color="primary"
                      disabled={partnerDisabled}
                      size={matches ? 'small' : 'medium'}
                    />
                  }
                  labelPlacement={matches ? 'end' : 'bottom'}
                  label={
                    <Box>
                      <img
                        src={assets.PARTNERS_LOGO_SMALL}
                        alt="CAPA Partners"
                        width={53}
                        height={30}
                      />
                      <CustomizedTypography
                        className="radioBoxTitle"
                        fontWeight="700"
                      >
                        고객 + 파트너 통합 회원가입
                      </CustomizedTypography>
                      <CustomizedTypography
                        className="radioBoxDetail"
                        fontWeight="normal"
                      >
                        {matches
                          ? '제조가 필요한 고객을 찾고 있어요'
                          : `제조가 필요한 고객을 찾고 있어요

                  `}
                      </CustomizedTypography>
                      {!matches && (
                        <>
                          <Divider className="radioBoxDivider" />
                          {benefits.map((benefit, index) => (
                            <RadioBoxItem
                              key={'benefit' + index}
                              className="radioBoxItem"
                            >
                              <CheckOutlined color="primary" />
                              <CustomizedTypography
                                variant="body2"
                                color="textPrimary"
                                fontWeight="500"
                                noWrap
                              >
                                {benefit}
                              </CustomizedTypography>
                            </RadioBoxItem>
                          ))}
                        </>
                      )}
                    </Box>
                  }
                />
              </Box>
            </RadioGroup>
          );
        }}
      />
    </Box>
  );
};

const RadioBoxItem = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  svg {
    width: 20px;
    height: 20px;
    margin-right: 8px;
  }
`;

export default AccountTypeSelector;
